import { ActionIcon, Box, Stack, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';

import { FEATURE } from '~/common/enums/feature.enum';
import { Button } from '~/domains/design-system/Button';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { ErrorAlert } from '~/domains/common/components/Alerts/Alerts';
import dt from '~/testing';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import { BalanceBox } from '~/domains/payments/components/BalanceBox';
import { RefundableBalanceBox } from './components/RefundableBalanceBox/RefundableBalanceBox';
import { PaymentMethodsModeSwitch } from './components/PaymentMethodsModeSwitch';

import { usePaymentTypeMode } from './hooks/usePaymentTypeMode';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
  },
  content: {
    flex: 1,
  },
  buttonWrapper: {
    marginTop: theme.other.spacing._24,
  },
}));

type WithdrawalFormPaymentTypeProps = {
  nextStep: () => void;
  isAeropayEnabled: boolean;
  onModalClose?: () => void;
  mode?: 'withdrawal' | 'refund';
  onModeChange?: (mode: 'withdrawal' | 'refund') => void;
};

export function WithdrawalFormPaymentType({
  nextStep,
  isAeropayEnabled,
  onModalClose,
  mode: propMode = 'withdrawal',
  onModeChange,
}: WithdrawalFormPaymentTypeProps) {
  const { t } = useTranslation('payments');
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  // Check for feature flag blocking
  const isWithdrawalBlocked = useIsFeatureEnabled(FEATURE.ENABLE_BETA_WITHDRAWAL_BLOCKER);

  // Use the simplified business logic hook
  const {
    isRefundMode,
    headerConfig,
    buttonConfig,
    balanceConfig,
    handlers,
    isLoadingPaymentMethods,
    feeDescriptions,
    // Refund data for PaymentMethodsModeSwitch
    refundableBreakdown,
    isFetchingRefundableBreakdown,
    hasConsistencyError,
  } = usePaymentTypeMode({
    mode: propMode,
    onModeChange,
    onModalClose,
    isAeropayEnabled,
  });

  // Handle feature flag blocking with proper modal structure
  if (isWithdrawalBlocked) {
    return (
      <Box className={classes.container}>
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={60} className={classes.content}>
          <SignUpHeader
            title={t('withdrawal.form.select.title')}
            copy={t('withdrawal.form.select.copy')}
            isLogoVisible={false}
          />
          <ErrorAlert>{t('withdrawal.errors.blockedByFeatureFlag')}</ErrorAlert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Back button for refund mode - only show on desktop */}
      {isRefundMode && !isMobile && (
        <ActionIcon
          onClick={() => handlers.onModeSwitch('withdrawal')}
          pos="absolute"
          top={2}
          left={-64}
          h={32}
          w={32}
        >
          <IconChevronLeft size={32} />
        </ActionIcon>
      )}

      {/* Content section that grows to push button to bottom */}
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24} className={classes.content}>
        {/* Header based on mode */}
        <SignUpHeader title={headerConfig.title} copy={headerConfig.copy} isLogoVisible={false} />

        {/* Balance Box - conditional rendering based on mode */}
        {balanceConfig.showRefundable && (
          <RefundableBalanceBox
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            refundableBalance={balanceConfig.refundableBalance!}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            isLoading={balanceConfig.isLoadingWalletBalance!}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onClick={balanceConfig.onRefundClick!}
          />
        )}

        {isRefundMode && <BalanceBox variant={balanceConfig.variant} />}

        {/* Payment Methods - using simplified props */}
        <PaymentMethodsModeSwitch
          mode={propMode}
          isAeropayEnabled={isAeropayEnabled}
          isLoadingPaymentMethods={isLoadingPaymentMethods}
          refundableBreakdown={refundableBreakdown}
          isFetchingRefundableBreakdown={isFetchingRefundableBreakdown}
          hasConsistencyError={hasConsistencyError}
          onWithdrawalMethodSelect={isMobile && !isRefundMode ? nextStep : undefined}
        />

        {/* Payment Descriptions - show service fee information for all paid methods */}
        {feeDescriptions.length > 0 && (
          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}>
            {feeDescriptions.map((feeInfo) => (
              <Text key={feeInfo.paymentType} variant="body-small" color="gray.6">
                {feeInfo.number}. {feeInfo.description}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Continue Button - pinned to bottom */}
      <Box className={classes.buttonWrapper}>
        <Tooltip
          disabled={isRefundMode}
          withArrow
          isRendered={buttonConfig.disabled}
          label={t('withdrawal.form.actions.tooltip.paymentType')}
        >
          <Box>
            <Button
              data-test-id={dt.payments.withdrawal.paymentTypeForm.continueButton}
              variant="primary-fill"
              size="large"
              type="button"
              fullWidth
              onClick={buttonConfig.onClick || nextStep}
              disabled={buttonConfig.disabled}
            >
              {buttonConfig.text}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
